import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, CalendarClock, CheckCircle2, FileText, Stethoscope } from 'lucide-react';
import type { PrescriptionWithRelations } from '@dpfs/shared';
import { prescriptionsService } from '@/services/prescriptions';
import { getApiError } from '@/services/api';
import { EmptyState, LoadingState, PageHeader, StatusBadge } from '@/components/ui';
import { localeFor } from '@/components/reports/chartTheme';

// The list endpoint also returns the fulfillment history (newest first).
type PortalPrescription = PrescriptionWithRelations & {
  fulfillments?: { id: string; fulfilledAt: string }[];
};

export function PatientPortal() {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const formatDate = (value: string | Date | null | undefined) =>
    value ? new Date(value).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['prescriptions', 'patient'],
    queryFn: () => prescriptionsService.list({ limit: 50 }),
    retry: (count, err) => !(axios.isAxiosError(err) && err.response?.status === 403) && count < 2,
  });

  const prescriptions = (data?.data ?? []) as PortalPrescription[];
  const isForbidden = axios.isAxiosError(error) && error.response?.status === 403;

  return (
    <div>
      <PageHeader title={t('nav.myPrescriptions')} description={t('patientPortal.description')} />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <div className="card">
          <EmptyState
            icon={AlertCircle}
            title={isForbidden ? t('patientPortal.unavailableTitle') : t('patientPortal.loadError')}
            description={isForbidden ? t('patientPortal.unavailableHint') : getApiError(error).message}
            action={
              !isForbidden && (
                <button type="button" className="btn-secondary" onClick={() => refetch()}>
                  {t('patientPortal.retry')}
                </button>
              )
            }
          />
        </div>
      ) : prescriptions.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={FileText}
            title={t('patientPortal.emptyTitle')}
            description={t('patientPortal.emptyHint')}
          />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {prescriptions.map((rx) => {
            const lastFulfillment = rx.fulfillments?.[0];
            const docType = (rx.documentType ?? 'MEDICATION').toLowerCase();
            return (
              <article key={rx.id} className="card flex flex-col p-5">
                <header className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="section-title">{t(`prescription.docTypes.${docType}`)}</p>
                    <h2 className="mt-1 font-semibold text-gray-900">
                      {t('prescription.title')}{' '}
                      <span className="font-mono text-sm text-gray-500">#{rx.id.slice(-8).toUpperCase()}</span>
                    </h2>
                  </div>
                  <StatusBadge status={rx.status} />
                </header>

                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <Stethoscope className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate">
                    {rx.prescriber
                      ? t('patientPortal.doctorName', {
                          name: `${rx.prescriber.firstName} ${rx.prescriber.lastName}`,
                        })
                      : '—'}
                  </span>
                </div>

                <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-100 bg-gray-50/60">
                  {(rx.items ?? []).map((item, index) => (
                    <li key={item.id ?? index} className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm">
                      <div className="min-w-0">
                        <p className="break-words font-medium text-gray-900">
                          {item.drug ? `${item.drug.tradeName} ${item.drug.strength ?? ''}`.trim() : item.itemLabel || '—'}
                        </p>
                        {[item.dosage, item.frequency, item.duration].some(Boolean) && (
                          <p className="mt-0.5 break-words text-xs text-gray-500">
                            {[item.dosage, item.frequency, item.duration].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {item.instructions && (
                          <p className="mt-0.5 break-words text-xs italic text-gray-500">{item.instructions}</p>
                        )}
                      </div>
                      {item.quantity != null && (
                        <span className="badge-gray shrink-0">
                          {item.quantity}{' '}
                          {item.quantityUnit
                            ? t(`prescription.units.${item.quantityUnit}`, { defaultValue: item.quantityUnit })
                            : '×'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                <dl className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                    <dt className="sr-only">{t('patientPortal.issuedOn')}</dt>
                    <dd>
                      {t('patientPortal.issuedOn')} {formatDate(rx.signedAt ?? rx.createdAt)}
                    </dd>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <CalendarClock className="h-4 w-4 shrink-0 text-gray-400" />
                    <dt className="sr-only">{t('patientPortal.validUntil')}</dt>
                    <dd>
                      {t('patientPortal.validUntil')} {formatDate(rx.expiresAt)}
                    </dd>
                  </div>
                  {lastFulfillment && (
                    <div className="flex items-center gap-2 text-success-700 sm:col-span-2">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      <dt className="sr-only">{t('patientPortal.lastFulfillment')}</dt>
                      <dd>
                        {t('patientPortal.lastFulfillment')} {formatDate(lastFulfillment.fulfilledAt)}
                      </dd>
                    </div>
                  )}
                </dl>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
